/**
 * IoT Service — 米家设备数据服务
 * 阶段一：使用 Mock 数据
 * 阶段二/四：切换为 /api/iot/devices 真实 API
 */

import type { MijiaDevice, AirConditionerState, ThermometerState } from '../components/MijiaDeviceCard';
import { apiFetch } from './apiClient';

// ── Mock Data ──

const MOCK_DEVICES: MijiaDevice[] = [
  {
    id: 'ac_living',
    name: '客厅空调',
    room: '客厅',
    type: 'air_conditioner',
    online: true,
    state: {
      power: true,
      currentTemp: 26.3,
      targetTemp: 25,
      mode: 'cool',
      fanSpeed: 'auto',
    } as AirConditionerState,
  },
  {
    id: 'ac_bedroom',
    name: '卧室空调',
    room: '主卧',
    type: 'air_conditioner',
    online: true,
    state: {
      power: false,
      currentTemp: 28.1,
      targetTemp: 26,
      mode: 'cool',
      fanSpeed: 'low',
    } as AirConditionerState,
  },
  {
    id: 'thermo_living',
    name: '客厅温湿度计',
    room: '客厅',
    type: 'thermometer',
    online: true,
    state: {
      temperature: 25.8,
      humidity: 58,
      battery: 87,
    } as ThermometerState,
  },
  {
    id: 'thermo_bedroom',
    name: '卧室温湿度计',
    room: '主卧',
    type: 'thermometer',
    online: true,
    state: {
      temperature: 27.2,
      humidity: 62,
      battery: 45,
    } as ThermometerState,
  },
];

// ── Simulated state changes ──

function simulateTempDrift(device: MijiaDevice): MijiaDevice {
  if (device.type !== 'air_conditioner') return device;
  const s = device.state as AirConditionerState;
  const drift = (Math.random() - 0.5) * 0.2;

  let newCurrent = s.currentTemp + drift;
  if (s.power) {
    const gap = s.targetTemp - s.currentTemp;
    // 制冷模式：当前温向目标降温；制热模式：升温
    if (s.mode === 'cool') {
      // gap > 0 → 需要降温，gap < 0 → 已低于目标，自然回升
      newCurrent -= gap * 0.01;  // 向目标收敛
    } else if (s.mode === 'heat') {
      newCurrent += gap * 0.01;   // 向目标收敛
    }
    // 其他模式（fan_only/dry）仅微漂移
  }

  // 常理约束：室内温度 15°C ~ 42°C
  newCurrent = Math.max(15, Math.min(42, Math.round(newCurrent * 10) / 10));
  return {
    ...device,
    state: { ...s, currentTemp: newCurrent },
  };
}

function simulateThermoDrift(device: MijiaDevice): MijiaDevice {
  if (device.type !== 'thermometer') return device;
  const s = device.state as ThermometerState;
  return {
    ...device,
    state: {
      ...s,
      temperature: Math.max(10, Math.min(45, Math.round((s.temperature + (Math.random() - 0.5) * 0.2) * 10) / 10)),
      humidity: Math.max(20, Math.min(95, Math.round(s.humidity + (Math.random() - 0.5) * 2))),
    },
  };
}

// ── Service Class ──

export interface IotStateUpdate {
  id: string;
  state: AirConditionerState | ThermometerState;
}

class IotService {
  private useMock = false; // false = 真实 HA API；true = Mock 数据
  private baseUrl = '/api/iot'; // apiRouter 挂载在 /api 下，所以这里 /api/iot 完整路径是 /api/iot
  private mockDevices: MijiaDevice[] = [...MOCK_DEVICES];

  /** 获取所有设备列表 */
  async getDevices(): Promise<MijiaDevice[]> {
    if (this.useMock) {
      this.mockDevices = this.mockDevices.map((d) => {
        if (d.type === 'air_conditioner') return simulateTempDrift(d);
        return simulateThermoDrift(d);
      });
      return [...this.mockDevices];
    }

    try {
      const r = await apiFetch(`${this.baseUrl}/devices`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.json();
    } catch (err) {
      console.error('[IoT] Failed to fetch devices:', err);
      return [];
    }
  }

  /** 发送控制命令到后端 POST /api/iot/control */
  private async sendControl(entityId: string, action: string, params: Record<string, any> = {}): Promise<void> {
    const r = await apiFetch(`${this.baseUrl}/control`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entity_id: entityId, action, params }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
      console.error(`[IoT] Control ${action} on ${entityId} failed:`, err);
    }
  }

  /** 切换设备电源 */
  async togglePower(id: string, powerOn: boolean): Promise<void> {
    if (this.useMock) {
      const device = this.mockDevices.find((d) => d.id === id);
      if (device && device.type === 'air_conditioner') {
        const s = device.state as AirConditionerState;
        device.state = { ...s, power: !s.power };
      }
      return;
    }
    await this.sendControl(id, powerOn ? 'turn_on' : 'turn_off');
  }

  /** 更新设备状态（模式/风速/目标温度），对比旧状态只发变化属性 */
  async updateState(id: string, newState: AirConditionerState | ThermometerState, oldState?: AirConditionerState | ThermometerState): Promise<void> {
    if (this.useMock) {
      const device = this.mockDevices.find((d) => d.id === id);
      if (device) device.state = newState;
      return;
    }

    // 空调：按属性变化分别发送控制命令
    if ('power' in newState && oldState && 'power' in oldState) {
      const ns = newState as AirConditionerState;
      const os = oldState as AirConditionerState;

      if (ns.mode !== os.mode) {
        const modeMap: Record<string, string> = { cool: 'cool', heat: 'heat', fan: 'fan_only', auto: 'auto', dry: 'dry' };
        await this.sendControl(id, 'set_hvac_mode', { mode: modeMap[ns.mode] || ns.mode });
      }
      if (ns.fanSpeed !== os.fanSpeed) {
        await this.sendControl(id, 'set_fan_mode', { fan_speed: ns.fanSpeed });
      }
      if (ns.targetTemp !== os.targetTemp) {
        await this.sendControl(id, 'set_temperature', { temperature: ns.targetTemp });
      }
    }
  }

  /** 切换到真实 API 模式 */
  setMockMode(useMock: boolean) {
    this.useMock = useMock;
  }
}

export const iotService = new IotService();
